// NamesScreen.jsx
import React, { useState, useMemo, useEffect } from "react";
import {
    Box,
    Grid,
    Paper,
    TextField,
    Typography,
    List,
    ListItem,
    ListItemText,
    Divider,
    Chip,
    Stack,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    Tooltip,
} from "@mui/material"; // MUI v7.3.4
import DownloadIcon from "@mui/icons-material/Download";

// Normalize text: lowercase and remove accents
const normalizeText = (text) => {
    if (!text) return "";
    return text
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
};

// Parse the comma-separated names into objects
const parseNamesWithGender = (namesInput, gender) => {
    return namesInput
        .split(",")
        .map((name) => name.trim())
        .filter((name) => name.length > 0)
        .map((name) => {
            const normalized = normalizeText(name);
            return {
                original: name,
                normalized,
                initial: normalized.charAt(0) || "",
                gender, // "male" | "female"
            };
        });
};
// Compute groups for a single key
const computeGroupsForKey = (keyRaw, names) => {
    const normalizedKey = normalizeText(keyRaw);
    const uniqueLetters = new Set(normalizedKey.split(""));

    const belongs = [];
    const notBelongs = [];

    names.forEach((n) => {
        if (!n.initial) return;
        if (uniqueLetters.has(n.initial)) {
            belongs.push(n);
        } else {
            notBelongs.push(n);
        }
    });

    const diff = Math.abs(belongs.length - notBelongs.length);

    return {
        key: keyRaw,
        normalizedKey,
        diff,
        belongs,
        notBelongs,
    };
};

const NamesScreen = () => {
    const [namesInputBoys, setNamesInputBoys] = useState(
        "Adrián, Alejandro, Daniel, Diego, Eduardo, Óscar, Jorge, Luis, Raúl, Ricardo, " +
            "Carlos, Fernando, Guillermo, Iván, Gabriel, Marcos, Miguel, Pablo, Borja, Victor,"
    );
    const [namesInputGirls, setNamesInputGirls] = useState(
        "Ana, Daniela, Olga, Eva, Julia, Lara, Laura, Noemi, Nuria, Raquel," +
            "Bea, Carla, Clara, María, Francisca, Paula, Pilar, Sandra, Sara, Susana"
    );
    const [keysInput, setKeysInput] = useState(
        "Alejandro,Pablo,Jorge,Puri,Miguel,Carla,Percy"
    );
    const [cardCountInput, setCardCountInput] = useState("40");
    const [activeDialog, setActiveDialog] = useState(null); // controls which dialog is open
    const [useSpanishNames, setUseSpanishNames] = useState(false);
    const [spanishNamesKeys, setSpanishNamesKeys] = useState([]);
    const [manualSelectedKey, setManualSelectedKey] = useState(null);
    const [isCyclic, setIsCyclic] = useState(true);
    const [cardsVersion, setCardsVersion] = useState(0);

    const {
        initialsCount,
        selectedKeyResult,
        sortedCandidates,
        uniqueNames,
        membershipByNormalized,
    } = useMemo(() => {
        const boys = parseNamesWithGender(namesInputBoys, "male");
        const girls = parseNamesWithGender(namesInputGirls, "female");
        const names = [...boys, ...girls];

        const counts = {};
        names.forEach((n) => {
            if (!n.initial) return;
            counts[n.initial] = (counts[n.initial] || 0) + 1;
        });

        if (names.length === 0) {
            return {
                initialsCount: counts,
                selectedKeyResult: null,
                sortedCandidates: [],
                uniqueNames: [],
                membershipByNormalized: {},
            };
        }

        // Stage 1: keys from input (initialized from DEFAULT_KEYS)
        const candidateKeys1 = keysInput
            .split(",")
            .map((k) => k.trim())
            .filter((k) => k.length > 0);

        const results1 = candidateKeys1.map((key, index) => ({
            ...computeGroupsForKey(key, names),
            order: index,
            source: "default",
        }));

        const winner1 = results1.find((r) => r.diff <= 1) || null;

        let allCandidates = [...results1];
        let selected = winner1;

        // Stage 2: use current names as potential keys if no key passed ±1 rule
        if (!winner1) {
            const seenNormalized = new Set();
            const candidateKeys2 = [];

            names.forEach((n) => {
                if (!n.normalized) return;
                if (!seenNormalized.has(n.normalized)) {
                    seenNormalized.add(n.normalized);
                    candidateKeys2.push(n.original);
                }
            });

            const results2 = candidateKeys2.map((key, index) => ({
                ...computeGroupsForKey(key, names),
                order: results1.length + index,
                source: "names",
            }));

            allCandidates = [...allCandidates, ...results2];

            const winner2 = results1.find((r) => r.diff <= 1) || null;
            if (winner2) {
                selected = winner2;
            }

            // Stage 3: optional Spanish names from CSVs as additional candidates
            if (!winner2 && useSpanishNames && spanishNamesKeys.length > 0) {
                const resultsSpanish = spanishNamesKeys.map((key, index) => ({
                    ...computeGroupsForKey(key, names),
                    order: allCandidates.length + index,
                    source: "spanish",
                }));

                allCandidates = [...allCandidates, ...resultsSpanish];

                const winnerSpanish =
                    resultsSpanish.find((r) => r.diff <= 1) || null;
                if (winnerSpanish) {
                    selected = winnerSpanish;
                }
            }
        } else {
            // Even if Stage 1 already found a winner, we still want to list Spanish
            // candidates in the sorted list when toggle is active.
            if (useSpanishNames && spanishNamesKeys.length > 0) {
                const resultsSpanish = spanishNamesKeys.map((key, index) => ({
                    ...computeGroupsForKey(key, names),
                    order: allCandidates.length + index,
                    source: "spanish",
                }));

                allCandidates = [...allCandidates, ...resultsSpanish];
            }
        }

        // Fallback: smallest diff if nobody has diff <= 1
        if (!selected && allCandidates.length > 0) {
            selected = allCandidates.reduce((best, current) => {
                if (!best) return current;
                if (current.diff < best.diff) return current;
                if (current.diff === best.diff && current.order < best.order) {
                    return current;
                }
                return best;
            }, null);
        }

        // Sort candidate keys by difference (and then by original order)
        const sorted = [...allCandidates].sort((a, b) => {
            if (a.diff !== b.diff) return a.diff - b.diff;
            return a.order - b.order;
        });

        const membership = {};
        if (selected) {
            selected.belongs.forEach((n) => {
                membership[n.normalized] = "belongs";
            });
            selected.notBelongs.forEach((n) => {
                membership[n.normalized] = "notBelongs";
            });
        }

        const uniqueMap = new Map();
        names.forEach((n) => {
            if (!uniqueMap.has(n.normalized)) {
                uniqueMap.set(n.normalized, n);
            }
        });
        const uniqueNamesArray = Array.from(uniqueMap.values());

        if (manualSelectedKey) {
            const forced = allCandidates.find(
                (c) => normalizeText(c.key) === normalizeText(manualSelectedKey)
            );

            if (forced) {
                selected = forced;
            }
        }

        return {
            initialsCount: counts,
            selectedKeyResult: selected,
            sortedCandidates: sorted,
            uniqueNames: uniqueNamesArray,
            membershipByNormalized: membership,
        };
    }, [
        namesInputBoys,
        namesInputGirls,
        keysInput,
        useSpanishNames,
        spanishNamesKeys,
        manualSelectedKey,
    ]);

    useEffect(() => {
        const NUMERO_NOMBRES = 500;
        const fetchSpanishNames = async () => {
            try {
                const urls = [
                    "https://raw.githubusercontent.com/marcboquet/spanish-names/refs/heads/master/mujeres.csv",
                    "https://raw.githubusercontent.com/marcboquet/spanish-names/refs/heads/master/hombres.csv",
                ];

                const allNames = [];
                const seenNormalized = new Set();

                for (let i = 0; i < urls.length; i += 1) {
                    const response = await fetch(urls[i]);
                    const text = await response.text();

                    const lines = text.split(/\r?\n/);
                    const dataLines = lines
                        .slice(1)
                        .filter((line) => line.trim().length > 0);

                    const firstNNames = dataLines.slice(0, NUMERO_NOMBRES);

                    firstNNames.forEach((line) => {
                        const parts = line.split(",");
                        const rawName = parts[0] ? parts[0].trim() : "";
                        if (rawName.length === 0) {
                            return;
                        }

                        const firstName = rawName.split(" ")[0].trim();
                        if (firstName.length === 0) {
                            return;
                        }

                        const normalized = normalizeText(firstName);
                        if (!normalized) {
                            return;
                        }

                        // Avoid duplicates for keys list, but membership is already stored
                        if (seenNormalized.has(normalized)) {
                            return;
                        }

                        seenNormalized.add(normalized);
                        allNames.push(firstName);
                    });
                }

                setSpanishNamesKeys(allNames);
            } catch (error) {
                console.error("Error loading Spanish names from CSV:", error);
            }
        };

        fetchSpanishNames();
    }, []);

    // Cards generation
    const cards = useMemo(() => {
        if (!selectedKeyResult || uniqueNames.length === 0) {
            return [];
        }

        const desiredCards = parseInt(cardCountInput, 10);
        const totalCards = Number.isNaN(desiredCards)
            ? 0
            : Math.max(0, desiredCards);

        if (totalCards === 0) {
            return [];
        }

        const enrichedNames = uniqueNames.map((n) => {
            const membership =
                membershipByNormalized[n.normalized] || "belongs";
            const gender = n.gender;
            return { ...n, membership, gender };
        });

        if (enrichedNames.length === 0) {
            return [];
        }

        // Four groups: belongs male/female, not-belongs male/female
        const belongMale = [];
        const belongFemale = [];
        const notMale = [];
        const notFemale = [];

        enrichedNames.forEach((n) => {
            if (n.membership === "belongs") {
                if (n.gender === "female") {
                    belongFemale.push(n);
                } else {
                    belongMale.push(n);
                }
            } else {
                if (n.gender === "female") {
                    notFemale.push(n);
                } else {
                    notMale.push(n);
                }
            }
        });

        const principalUsage = {};
        const secondaryUsage = {};
        const totalUsage = {};
        enrichedNames.forEach((n) => {
            principalUsage[n.normalized] = 0;
            secondaryUsage[n.normalized] = 0;
            totalUsage[n.normalized] = 0;
        });

        const randomOrderMap = {};
        enrichedNames.forEach((n) => {
            randomOrderMap[n.normalized] = Math.random();
        });

        const allNames = enrichedNames;

        // Updated: pick candidates trying to balance total usage AND card initials
        const pickFromCandidates = (
            candidates,
            count,
            excludeSet,
            cardInitialsCount
        ) => {
            const selected = [];

            const sorted = [...candidates]
                .filter((n) => !excludeSet.has(n.normalized))
                .sort((a, b) => {
                    const ta = totalUsage[a.normalized];
                    const tb = totalUsage[b.normalized];

                    // First criteria: lower total usage (principal + secondary)
                    if (ta !== tb) {
                        return ta - tb;
                    }

                    // Second criteria: prefer names whose initial is less used in this card
                    if (cardInitialsCount) {
                        const ia = cardInitialsCount[a.initial] || 0;
                        const ib = cardInitialsCount[b.initial] || 0;

                        if (ia !== ib) {
                            return ia - ib;
                        }
                    }

                    // Third criteria: break ties using a random order
                    return (
                        randomOrderMap[a.normalized] -
                        randomOrderMap[b.normalized]
                    );
                });

            for (
                let i = 0;
                i < sorted.length && selected.length < count;
                i += 1
            ) {
                const n = sorted[i];

                // Optional constraint: try not to have more than 2 names
                // with the same initial on the same card
                if (cardInitialsCount && n.initial) {
                    const currentInitialCount =
                        cardInitialsCount[n.initial] || 0;

                    if (currentInitialCount >= 2) {
                        // Check if there is any alternative candidate with this initial < 2
                        const hasAlternative = sorted.slice(i + 1).some((m) => {
                            if (excludeSet.has(m.normalized)) {
                                return false;
                            }
                            if (!m.initial) {
                                return false;
                            }
                            const altCount = cardInitialsCount[m.initial] || 0;
                            return altCount < 2;
                        });

                        // If there is an alternative, skip this candidate
                        if (hasAlternative) {
                            continue;
                        }
                    }
                }

                selected.push(n);
                excludeSet.add(n.normalized);

                // Track how many times this initial appears on this card
                if (cardInitialsCount && n.initial) {
                    cardInitialsCount[n.initial] =
                        (cardInitialsCount[n.initial] || 0) + 1;
                }

                // Update secondary and total usage when a name is picked as secondary
                secondaryUsage[n.normalized] += 1;
                totalUsage[n.normalized] += 1;
            }

            return selected;
        };

        const belongGroup = [...belongMale, ...belongFemale];
        const notGroup = [...notMale, ...notFemale];

        // Pick a principal from a specific membership group, balancing usage
        // and trying not to repeat the previous principal's initial.
        // extraForbiddenInitial se usa para evitar patrones A - ❌ - A.
        const pickPrincipalFromGroup = (
            group,
            lastPrincipal,
            extraForbiddenInitial
        ) => {
            if (!group || group.length === 0) {
                return null;
            }

            const forbiddenInitial = lastPrincipal
                ? lastPrincipal.initial
                : null;

            let minUsage = Infinity;
            group.forEach((n) => {
                const u = principalUsage[n.normalized];
                if (u < minUsage) {
                    minUsage = u;
                }
            });

            const leastUsed = group.filter(
                (n) => principalUsage[n.normalized] === minUsage
            );

            let best = null;

            leastUsed.forEach((n) => {
                const sameInitial =
                    forbiddenInitial && n.initial === forbiddenInitial;
                const sameExtra =
                    extraForbiddenInitial &&
                    n.initial === extraForbiddenInitial;

                if (!best) {
                    best = n;
                    return;
                }

                const bestSameInitial =
                    forbiddenInitial && best.initial === forbiddenInitial;
                const bestSameExtra =
                    extraForbiddenInitial &&
                    best.initial === extraForbiddenInitial;

                // 1) Preferir nombres que NO tengan la extraForbiddenInitial
                if (bestSameExtra && !sameExtra) {
                    best = n;
                    return;
                }
                if (!bestSameExtra && sameExtra) {
                    return;
                }

                // 2) Regla original: preferir no repetir la inicial del anterior
                if (bestSameInitial && !sameInitial) {
                    best = n;
                    return;
                }
                if (!bestSameInitial && sameInitial) {
                    return;
                }

                // 3) Desempatar con totalUsage y randomOrderMap
                const bestTotal = totalUsage[best.normalized];
                const currentTotal = totalUsage[n.normalized];

                if (currentTotal < bestTotal) {
                    best = n;
                } else if (currentTotal === bestTotal) {
                    if (
                        randomOrderMap[n.normalized] <
                        randomOrderMap[best.normalized]
                    ) {
                        best = n;
                    }
                }
            });

            // Si todos los candidatos comparten la extraForbiddenInitial,
            // al menos devolvemos alguno para no bloquear la generación.
            return best || leastUsed[0] || null;
        };

        const orderByRandom = (arr) => {
            return [...arr].sort(
                (a, b) =>
                    randomOrderMap[a.normalized] - randomOrderMap[b.normalized]
            );
        };

        // NamesScreen.jsx
        const fixConsecutivePairs = (list, isCyclicFix = false) => {
            const arr = [...list];
            const n = arr.length;

            if (n < 2) return arr;

            const sameInitial = (x, y) => {
                if (!x || !y) return false;
                if (!x.initial || !y.initial) return false;
                return x.initial === y.initial;
            };

            const isBadAt = (idxA, idxB) => sameInitial(arr[idxA], arr[idxB]);

            const tryFixAtIndex = (i) => {
                // We want to fix (i, i+1)
                const j = i + 1;
                if (j >= n) return false;
                if (!isBadAt(i, j)) return false;

                // Find a swap candidate further ahead that breaks the repetition
                for (let k = j + 1; k < n; k += 1) {
                    // After swapping arr[j] <-> arr[k], we need:
                    // 1) arr[i] not same initial with new arr[j] (which is old arr[k])
                    // 2) new arr[k] (old arr[j]) not same initial with arr[k-1] (unless k-1 === j)
                    // 3) new arr[j] not same initial with arr[j+1] (old arr[j+1]) if exists
                    const oldJ = arr[j];
                    const oldK = arr[k];

                    const prevOfJ = arr[i];
                    const nextOfJ = j + 1 < n ? arr[j + 1] : null;

                    const prevOfK = k - 1 >= 0 ? arr[k - 1] : null;
                    const nextOfK = k + 1 < n ? arr[k + 1] : null;

                    // Check constraints after swap
                    if (sameInitial(prevOfJ, oldK)) continue;
                    if (nextOfJ && sameInitial(oldK, nextOfJ)) continue;

                    // For position k, oldJ will be there
                    // If k-1 is exactly j, then prevOfK is oldK after swap, so we should compare oldJ with oldK
                    const effectivePrevOfK = k - 1 === j ? oldK : prevOfK;
                    if (effectivePrevOfK && sameInitial(effectivePrevOfK, oldJ))
                        continue;
                    if (nextOfK && sameInitial(oldJ, nextOfK)) continue;

                    // Swap
                    arr[j] = oldK;
                    arr[k] = oldJ;
                    return true;
                }

                return false;
            };

            // Multiple passes because fixing one place can create another issue later
            for (let pass = 0; pass < n * 3; pass += 1) {
                let changed = false;

                for (let i = 0; i < n - 1; i += 1) {
                    const fixed = tryFixAtIndex(i);
                    if (fixed) changed = true;
                }

                if (!changed) break;
            }

            // Optional: also avoid last-first repetition if you want a "cyclic-safe" ordering
            if (isCyclicFix && n >= 3 && sameInitial(arr[0], arr[n - 1])) {
                // Try to swap last with some middle element
                for (let k = 1; k < n - 1; k += 1) {
                    if (
                        !sameInitial(arr[0], arr[k]) &&
                        !sameInitial(arr[k], arr[n - 2])
                    ) {
                        const tmp = arr[n - 1];
                        arr[n - 1] = arr[k];
                        arr[k] = tmp;
                        break;
                    }
                }
            }

            return arr;
        };

        let belongOrder = fixConsecutivePairs(orderByRandom(belongGroup), true);
        let notOrder = fixConsecutivePairs(orderByRandom(notGroup), true);

        const principals = [];

        const hasBelongs = belongOrder.length > 0;
        const hasNotBelongs = notOrder.length > 0;

        let bi = 0;
        let ni = 0;

        for (let i = 0; i < totalCards; i += 1) {
            let chosen = null;

            // If we need more cards than names, reshuffle and keep going
            if (hasBelongs && bi >= belongOrder.length) {
                bi = 0;
                belongOrder = fixConsecutivePairs(
                    orderByRandom(belongGroup),
                    true
                );
            }
            if (hasNotBelongs && ni >= notOrder.length) {
                ni = 0;
                notOrder = fixConsecutivePairs(orderByRandom(notGroup), true);
            }

            if (hasBelongs && hasNotBelongs) {
                const wantBelongs = i % 2 === 0;

                if (wantBelongs) {
                    chosen = belongOrder[bi] || notOrder[ni] || null;
                    if (chosen && chosen.membership === "belongs") bi += 1;
                    else if (chosen) ni += 1;
                } else {
                    chosen = notOrder[ni] || belongOrder[bi] || null;
                    if (chosen && chosen.membership === "notBelongs") ni += 1;
                    else if (chosen) bi += 1;
                }
            } else if (hasBelongs) {
                chosen = belongOrder[bi] || null;
                if (chosen) bi += 1;
            } else if (hasNotBelongs) {
                chosen = notOrder[ni] || null;
                if (chosen) ni += 1;
            }

            if (!chosen) break;

            principals.push(chosen);
            principalUsage[chosen.normalized] += 1;
            totalUsage[chosen.normalized] += 1;
        }

        const cards = [];

        const getInitialRepetitionForCard = (principal, secondaries) => {
            const counts = {};

            const add = (person) => {
                if (!person || !person.initial) return;
                counts[person.initial] = (counts[person.initial] || 0) + 1;
            };

            add(principal);
            secondaries.forEach(add);

            let score = 0;
            let repetitions = 0;
            Object.values(counts).forEach((value) => {
                if (value >= 2) {
                    repetitions += 1;
                    score = Math.max(value, score);
                }
            });
            if (repetitions > 1) score += 0.5;

            return score;
        };

        const hasDuplicateNamesInCard = (principal, secondaries) => {
            // Detect if the same person (normalized) appears more than once
            const seen = new Set();

            if (principal && principal.normalized) {
                seen.add(principal.normalized);
            }

            for (let i = 0; i < secondaries.length; i += 1) {
                const person = secondaries[i];
                if (!person || !person.normalized) {
                    continue;
                }
                if (seen.has(person.normalized)) {
                    // Duplicate person in this card
                    return true;
                }
                seen.add(person.normalized);
            }

            return false;
        };
        const optimizeSecondariesBySwapping = () => {
            const MAX_PASSES_PER_LEVEL = 3000;

            const canSwapIndex = (card, index) => {
                if (!card.secondaries) {
                    return false;
                }
                if (index <= 0 || index >= card.secondaries.length - 1) {
                    return false;
                }
                return !!card.secondaries[index];
            };

            const levelThresholds = [3.5, 3, 2.5, 2];
            const badThreshold = 2.5;

            const scores = cards.map((card) => card.maxInitialRepetition || 0);

            const trySwapBetweenCards = (indexA, indexB) => {
                const cardA = cards[indexA];
                const cardB = cards[indexB];

                if (
                    !cardA.secondaries ||
                    !cardB.secondaries ||
                    cardA.secondaries.length < 3 ||
                    cardB.secondaries.length < 3
                ) {
                    return false;
                }

                for (
                    let idxA = 1;
                    idxA < cardA.secondaries.length - 1;
                    idxA += 1
                ) {
                    if (!canSwapIndex(cardA, idxA)) {
                        continue;
                    }
                    const secA = cardA.secondaries[idxA];
                    if (!secA) {
                        continue;
                    }

                    for (
                        let idxB = 1;
                        idxB < cardB.secondaries.length - 1;
                        idxB += 1
                    ) {
                        if (!canSwapIndex(cardB, idxB)) {
                            continue;
                        }
                        const secB = cardB.secondaries[idxB];
                        if (!secB) {
                            continue;
                        }

                        // Only swap if gender and membership (belongs/not) match
                        if (
                            secA.gender !== secB.gender ||
                            secA.membership !== secB.membership
                        ) {
                            continue;
                        }

                        // Simulate swap
                        // Simulate swap
                        const newSecondariesA = [...cardA.secondaries];
                        const newSecondariesB = [...cardB.secondaries];

                        newSecondariesA[idxA] = secB;
                        newSecondariesB[idxB] = secA;

                        // Do not allow swaps that introduce duplicated names
                        const hasDuplicatesA = hasDuplicateNamesInCard(
                            cardA.principal,
                            newSecondariesA
                        );
                        const hasDuplicatesB = hasDuplicateNamesInCard(
                            cardB.principal,
                            newSecondariesB
                        );

                        if (hasDuplicatesA || hasDuplicatesB) {
                            // This swap would create a card with repeated names,
                            // so we skip it directly.
                            continue;
                        }

                        const oldScoreA = cardA.maxInitialRepetition;
                        const oldScoreB = cardB.maxInitialRepetition;

                        const newScoreA = getInitialRepetitionForCard(
                            cardA.principal,
                            newSecondariesA
                        );
                        const newScoreB = getInitialRepetitionForCard(
                            cardB.principal,
                            newSecondariesB
                        );

                        const oldPairScore = oldScoreA + oldScoreB;
                        const newPairScore = newScoreA + newScoreB;

                        // Accept swap only if the combined score improves
                        if (newPairScore < oldPairScore) {
                            cardA.secondaries = newSecondariesA;
                            cardA.maxInitialRepetition = newScoreA;

                            cardB.secondaries = newSecondariesB;
                            cardB.maxInitialRepetition = newScoreB;

                            scores[indexA] = newScoreA;
                            scores[indexB] = newScoreB;

                            return true;
                        }
                    }
                }

                return false;
            };

            levelThresholds.forEach((levelThreshold) => {
                for (let pass = 0; pass < MAX_PASSES_PER_LEVEL; pass += 1) {
                    let passImproved = false;

                    for (let i = 0; i < cards.length; i += 1) {
                        if (scores[i] < levelThreshold) {
                            continue;
                        }

                        let improvedThisCard = false;

                        // Stage 1: try to improve this bad card using other "bad" cards first
                        for (
                            let j = 0;
                            j < cards.length && !improvedThisCard;
                            j += 1
                        ) {
                            if (i === j) {
                                continue;
                            }
                            if (scores[j] < badThreshold) {
                                // In this stage we only want other bad cards
                                continue;
                            }

                            const improved = trySwapBetweenCards(i, j);
                            if (improved) {
                                improvedThisCard = true;
                                passImproved = true;
                            }
                        }

                        // Stage 2: if no luck with bad cards, allow using good cards as donors
                        if (!improvedThisCard) {
                            for (
                                let j = 0;
                                j < cards.length && !improvedThisCard;
                                j += 1
                            ) {
                                if (i === j) {
                                    continue;
                                }
                                if (scores[j] >= badThreshold) {
                                    // Here we only want good cards (below badThreshold)
                                    continue;
                                }

                                const improved = trySwapBetweenCards(i, j);
                                if (improved) {
                                    improvedThisCard = true;
                                    passImproved = true;
                                }
                            }
                        }
                    }

                    // If we could not improve anything at this level in this pass,
                    // there is no point in continuing further passes for this level.
                    if (!passImproved) {
                        break;
                    }
                }
            });
        };

        principals.forEach((principal, index) => {
            const principalMembership = principal.membership;
            const principalGender = principal.gender;

            const exclude = new Set([principal.normalized]);

            const cardInitialsCount = {};
            if (principal.initial) {
                cardInitialsCount[principal.initial] = 1;
            }

            // Base gender count: only the principal for now
            let maleCount = principalGender === "male" ? 1 : 0;
            let femaleCount = principalGender === "female" ? 1 : 0;

            const oppositeMembership =
                principalMembership === "belongs" ? "notBelongs" : "belongs";

            const oppositeMale =
                oppositeMembership === "belongs" ? belongMale : notMale;
            const oppositeFemale =
                oppositeMembership === "belongs" ? belongFemale : notFemale;

            const sameMale =
                principalMembership === "belongs" ? belongMale : notMale;
            const sameFemale =
                principalMembership === "belongs" ? belongFemale : notFemale;

            let secondaries = [];

            // Cyclic mode: first secondary is next principal, last is previous principal
            let cyclicPrevPrincipal = null;
            let shouldIncludePrev = false;

            if (isCyclic && principals.length > 1) {
                const nextPrincipal =
                    principals[(index + 1) % principals.length];
                const prevPrincipal =
                    principals[
                        (index - 1 + principals.length) % principals.length
                    ];

                if (
                    nextPrincipal &&
                    nextPrincipal.normalized !== principal.normalized
                ) {
                    // first secondary = next principal
                    secondaries.push(nextPrincipal);
                    exclude.add(nextPrincipal.normalized);

                    secondaryUsage[nextPrincipal.normalized] += 1;
                    totalUsage[nextPrincipal.normalized] += 1;

                    // Update gender counts with the next principal
                    if (nextPrincipal.gender === "male") {
                        maleCount += 1;
                    } else if (nextPrincipal.gender === "female") {
                        femaleCount += 1;
                    }

                    if (nextPrincipal.initial) {
                        cardInitialsCount[nextPrincipal.initial] =
                            (cardInitialsCount[nextPrincipal.initial] || 0) + 1;
                    }
                }

                if (
                    prevPrincipal &&
                    prevPrincipal.normalized !== principal.normalized &&
                    (!nextPrincipal ||
                        prevPrincipal.normalized !== nextPrincipal.normalized)
                ) {
                    // last secondary = previous principal (will be added at the end)
                    cyclicPrevPrincipal = prevPrincipal;
                    shouldIncludePrev = true;
                    exclude.add(prevPrincipal.normalized);

                    // Pre-book usage so balancing sees that this name
                    // will also appear as secondary on this card
                    secondaryUsage[prevPrincipal.normalized] += 1;
                    totalUsage[prevPrincipal.normalized] += 1;

                    if (prevPrincipal.initial) {
                        cardInitialsCount[prevPrincipal.initial] =
                            (cardInitialsCount[prevPrincipal.initial] || 0) + 1;
                    }
                }
            }

            const targetSecondariesCount = 5;

            // If we will add the previous principal at the end, take its gender
            const reservedPrevGender =
                shouldIncludePrev && cyclicPrevPrincipal
                    ? cyclicPrevPrincipal.gender
                    : null;

            // Future gender counts once the reserved previous principal is added
            const futureMaleCount =
                maleCount + (reservedPrevGender === "male" ? 1 : 0);
            const futureFemaleCount =
                femaleCount + (reservedPrevGender === "female" ? 1 : 0);

            // We compute how many males/females we still need so that
            // principal + cyclic neighbours + fills ≈ 3 boys / 3 girls
            let neededMale = Math.max(0, 3 - futureMaleCount);
            let neededFemale = Math.max(0, 3 - futureFemaleCount);

            let remainingSlots =
                targetSecondariesCount -
                secondaries.length -
                (shouldIncludePrev ? 1 : 0);

            const pickAndAppend = (sourceArray, count) => {
                if (remainingSlots <= 0 || count <= 0) {
                    return;
                }

                const toPick = Math.min(count, remainingSlots);
                const picked = pickFromCandidates(
                    sourceArray,
                    toPick,
                    exclude,
                    cardInitialsCount
                );

                secondaries = [...secondaries, ...picked];

                remainingSlots =
                    targetSecondariesCount -
                    secondaries.length -
                    (shouldIncludePrev ? 1 : 0);
            };

            // First try: opposite membership, keeping gender balance
            if (neededMale > 0) {
                pickAndAppend(oppositeMale, neededMale);
            }

            if (neededFemale > 0) {
                pickAndAppend(oppositeFemale, neededFemale);
            }

            // Relax membership constraint if needed
            if (secondaries.length + (shouldIncludePrev ? 1 : 0) < 5) {
                const oppositeAll = [...oppositeMale, ...oppositeFemale];
                const remaining =
                    targetSecondariesCount -
                    secondaries.length -
                    (shouldIncludePrev ? 1 : 0);
                pickAndAppend(oppositeAll, remaining);
            }

            if (secondaries.length + (shouldIncludePrev ? 1 : 0) < 5) {
                const sameAll = [...sameMale, ...sameFemale];
                const remaining =
                    targetSecondariesCount -
                    secondaries.length -
                    (shouldIncludePrev ? 1 : 0);
                pickAndAppend(sameAll, remaining);
            }

            // Last fallback: any name
            if (secondaries.length + (shouldIncludePrev ? 1 : 0) < 5) {
                const remaining =
                    targetSecondariesCount -
                    secondaries.length -
                    (shouldIncludePrev ? 1 : 0);
                pickAndAppend(allNames, remaining);
            }

            // Append previous principal as last secondary if cyclic
            if (shouldIncludePrev && cyclicPrevPrincipal) {
                secondaries.push(cyclicPrevPrincipal);
                // secondaryUsage / totalUsage were already updated above
            }

            // Recompute gender counts on the final card (for display only)
            maleCount = principalGender === "male" ? 1 : 0;
            femaleCount = principalGender === "female" ? 1 : 0;

            secondaries.forEach((n) => {
                if (n.gender === "male") maleCount += 1;
                if (n.gender === "female") femaleCount += 1;
            });

            cards.push({
                id: index + 1,
                principal,
                principalMembership,
                secondaries,
                maleCount,
                femaleCount,
                maxInitialRepetition: getInitialRepetitionForCard(
                    principal,
                    secondaries
                ),
                cardsVersion,
            });
        });

        Array.from({ length: 2 }).forEach(() => {
            // Second pass: rebuild secondaries keeping principal and cyclic neighbours
            principals.forEach((principal, index) => {
                // If you also want this when not cyclic, remove this early return
                if (!isCyclic || principals.length <= 1) {
                    return;
                }

                const principalGender = principal.gender;
                const principalMembership = principal.membership;

                const exclude = new Set([principal.normalized]);

                const cardInitialsCount = {};
                if (principal.initial) {
                    cardInitialsCount[principal.initial] = 1;
                }

                // Start gender counts from principal
                let maleCount = principalGender === "male" ? 1 : 0;
                let femaleCount = principalGender === "female" ? 1 : 0;

                let secondaries = [];

                // Cyclic neighbours: next principal (first secondary), previous principal (last)
                const nextPrincipal =
                    principals[(index + 1) % principals.length];
                const prevPrincipal =
                    principals[
                        (index - 1 + principals.length) % principals.length
                    ];

                let cyclicPrevPrincipal = null;
                let shouldIncludePrev = false;

                if (
                    nextPrincipal &&
                    nextPrincipal.normalized !== principal.normalized
                ) {
                    // Keep next principal as first secondary
                    secondaries.push(nextPrincipal);
                    exclude.add(nextPrincipal.normalized);

                    if (nextPrincipal.gender === "male") maleCount += 1;
                    if (nextPrincipal.gender === "female") femaleCount += 1;
                }

                if (
                    prevPrincipal &&
                    prevPrincipal.normalized !== principal.normalized &&
                    (!nextPrincipal ||
                        prevPrincipal.normalized !== nextPrincipal.normalized)
                ) {
                    // Keep previous principal as last secondary (reserved for the end)
                    cyclicPrevPrincipal = prevPrincipal;
                    shouldIncludePrev = true;
                    exclude.add(prevPrincipal.normalized);

                    if (prevPrincipal.initial) {
                        cardInitialsCount[prevPrincipal.initial] =
                            (cardInitialsCount[prevPrincipal.initial] || 0) + 1;
                    }

                    // Important: we do NOT touch usage counters here,
                    // we keep the usage from the first pass.
                }

                const oppositeMembership =
                    principalMembership === "belongs"
                        ? "notBelongs"
                        : "belongs";

                const oppositeMale =
                    oppositeMembership === "belongs" ? belongMale : notMale;
                const oppositeFemale =
                    oppositeMembership === "belongs" ? belongFemale : notFemale;

                const sameMale =
                    principalMembership === "belongs" ? belongMale : notMale;
                const sameFemale =
                    principalMembership === "belongs"
                        ? belongFemale
                        : notFemale;

                const targetSecondariesCount = 5;

                // We consider that the previous principal will be added at the end
                const reservedPrevGender =
                    shouldIncludePrev && cyclicPrevPrincipal
                        ? cyclicPrevPrincipal.gender
                        : null;

                const futureMaleCount =
                    maleCount + (reservedPrevGender === "male" ? 1 : 0);
                const futureFemaleCount =
                    femaleCount + (reservedPrevGender === "female" ? 1 : 0);

                let neededMale = Math.max(0, 3 - futureMaleCount);
                let neededFemale = Math.max(0, 3 - futureFemaleCount);

                let remainingSlots =
                    targetSecondariesCount -
                    secondaries.length -
                    (shouldIncludePrev ? 1 : 0);

                const localPickAndAppend = (sourceArray, count) => {
                    if (remainingSlots <= 0 || count <= 0) {
                        return;
                    }

                    const toPick = Math.min(count, remainingSlots);
                    // This uses your existing pickFromCandidates,
                    // which updates secondaryUsage and totalUsage
                    const picked = pickFromCandidates(
                        sourceArray,
                        toPick,
                        exclude,
                        cardInitialsCount
                    );

                    secondaries = [...secondaries, ...picked];

                    remainingSlots =
                        targetSecondariesCount -
                        secondaries.length -
                        (shouldIncludePrev ? 1 : 0);
                };

                // First try: opposite membership, with gender balance
                if (neededMale > 0) {
                    localPickAndAppend(oppositeMale, neededMale);
                }

                if (neededFemale > 0) {
                    localPickAndAppend(oppositeFemale, neededFemale);
                }

                // Relax membership if we still have free slots
                if (secondaries.length + (shouldIncludePrev ? 1 : 0) < 5) {
                    const oppositeAll = [...oppositeMale, ...oppositeFemale];
                    const remaining =
                        targetSecondariesCount -
                        secondaries.length -
                        (shouldIncludePrev ? 1 : 0);
                    localPickAndAppend(oppositeAll, remaining);
                }

                if (secondaries.length + (shouldIncludePrev ? 1 : 0) < 5) {
                    const sameAll = [...sameMale, ...sameFemale];
                    const remaining =
                        targetSecondariesCount -
                        secondaries.length -
                        (shouldIncludePrev ? 1 : 0);
                    localPickAndAppend(sameAll, remaining);
                }

                // Last fallback: any name
                if (secondaries.length + (shouldIncludePrev ? 1 : 0) < 5) {
                    const remaining =
                        targetSecondariesCount -
                        secondaries.length -
                        (shouldIncludePrev ? 1 : 0);
                    localPickAndAppend(allNames, remaining);
                }

                // Now append the previous principal as last secondary (if cyclic)
                if (shouldIncludePrev && cyclicPrevPrincipal) {
                    secondaries.push(cyclicPrevPrincipal);
                    // Again: no usage updates here, we keep the ones from the first pass
                }

                // Recompute gender counts for display, based on the final card
                maleCount = principalGender === "male" ? 1 : 0;
                femaleCount = principalGender === "female" ? 1 : 0;

                secondaries.forEach((n) => {
                    if (n.gender === "male") maleCount += 1;
                    if (n.gender === "female") femaleCount += 1;
                });

                // Replace card data with the re-evaluated secondaries
                cards[index] = {
                    ...cards[index],
                    secondaries,
                    maleCount,
                    femaleCount,
                    maxInitialRepetition: getInitialRepetitionForCard(
                        principal,
                        secondaries
                    ),
                };
            });
        });
        optimizeSecondariesBySwapping();

        return cards;
    }, [
        selectedKeyResult,
        uniqueNames,
        membershipByNormalized,
        cardCountInput,
        isCyclic,
        cardsVersion,
    ]);

    const hasR2OrMore = useMemo(() => {
        if (!cards || cards.length === 0) return false;
        return cards.some((c) => (c?.maxInitialRepetition ?? 0) >= 2);
    }, [cards]);

    const nameUsage = useMemo(() => {
        if (!cards || cards.length === 0) {
            return [];
        }

        const usageMap = new Map();

        const registerUsage = (nameObj, role) => {
            if (!nameObj || !nameObj.normalized) return;

            const key = nameObj.normalized;
            let entry = usageMap.get(key);

            if (!entry) {
                entry = {
                    normalized: key,
                    original: nameObj.original,
                    principalCount: 0,
                    secondaryCount: 0,
                    totalCount: 0,
                };
            }

            if (role === "principal") {
                entry.principalCount += 1;
            } else if (role === "secondary") {
                entry.secondaryCount += 1;
            }

            entry.totalCount = entry.principalCount + entry.secondaryCount;

            usageMap.set(key, entry);
        };

        cards.forEach((card) => {
            registerUsage(card.principal, "principal");
            card.secondaries.forEach((n) => registerUsage(n, "secondary"));
        });

        const result = Array.from(usageMap.values());

        result.sort((a, b) => {
            if (b.totalCount !== a.totalCount) {
                return b.totalCount - a.totalCount;
            }
            return a.original.localeCompare(b.original);
        });

        return result;
    }, [cards]);

    const initialsEntries = Object.entries(initialsCount).sort((a, b) =>
        a[0].localeCompare(b[0])
    );

    const handleCloseDialog = () => {
        setActiveDialog(null);
    };

    // This helper defines dialog max width based on content type
    const getDialogMaxWidth = () => {
        if (!activeDialog) return "md";
        if (activeDialog === "initials") return "xs";
        if (activeDialog === "selectedKey") return "md";
        if (activeDialog === "keysList") return "sm";
        if (activeDialog === "cards") return "lg";
        if (activeDialog === "usage") return "sm";
        return "md";
    };

    const handleExportCardsAsText = () => {
        // If there is really nothing useful, skip
        if (
            (!cards || cards.length === 0) &&
            (!uniqueNames || uniqueNames.length === 0) &&
            !selectedKeyResult
        ) {
            return;
        }

        const lines = [];

        // Header
        lines.push("📄 Tarjetas de nombres – Exportación");
        const now = new Date();
        lines.push(`Fecha y hora de exportación: ${now.toLocaleString()}`);
        lines.push("");

        // ───────────────────────────────── Clave seleccionada
        lines.push("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
        lines.push("🗝️ Clave seleccionada");

        if (selectedKeyResult) {
            lines.push(
                `Clave: ${selectedKeyResult.key}  |  Diferencia de grupos: ±${selectedKeyResult.diff}`
            );
            lines.push(
                `Nombres que pertenecen a la clave (✅): ${selectedKeyResult.belongs.length}`
            );
            lines.push(
                `Nombres que NO pertenecen a la clave (❌): ${selectedKeyResult.notBelongs.length}`
            );
        } else {
            lines.push("No hay clave seleccionada actualmente.");
        }

        lines.push("");

        // ───────────────────────────────── Nombres usados
        lines.push("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
        lines.push("🧑‍🤝‍🧑 Nombres usados");

        if (uniqueNames && uniqueNames.length > 0) {
            const enrichedNames = uniqueNames
                .map((n) => {
                    const membership =
                        membershipByNormalized[n.normalized] || "belongs";
                    const gender = n.gender;
                    return { ...n, membership, gender };
                })
                .sort((a, b) =>
                    a.original.localeCompare(b.original, "es", {
                        sensitivity: "base",
                    })
                );

            enrichedNames.forEach((n, index) => {
                const genderEmoji = n.gender === "female" ? "♀️" : "♂️";
                const membershipEmoji =
                    n.membership === "belongs" ? "✅" : "❌";
                const membershipLabel =
                    n.membership === "belongs"
                        ? "Pertenece a la clave"
                        : "No pertenece a la clave";

                lines.push(
                    `${index + 1}. ${
                        n.original
                    } ${genderEmoji} ${membershipEmoji} ${membershipLabel}`
                );
            });
        } else {
            lines.push("No hay nombres procesados.");
        }

        lines.push("");

        // ───────────────────────────────── Distribución por inicial
        lines.push("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
        lines.push("🔤 Distribución por inicial");

        if (initialsEntries && initialsEntries.length > 0) {
            initialsEntries.forEach(([initial, count]) => {
                lines.push(`• ${initial.toUpperCase()}: ${count}`);
            });
        } else {
            lines.push("Sin datos de iniciales.");
        }

        lines.push("");

        // ───────────────────────────────── Repeticiones
        lines.push("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
        lines.push("🔁 Repeticiones por nombre");

        if (nameUsage && nameUsage.length > 0) {
            nameUsage.forEach((u, index) => {
                lines.push(
                    `${index + 1}. ${u.original} – Total: ${
                        u.totalCount
                    }  |  🃏 Principal: ${
                        u.principalCount
                    }  |  👥 Secundario: ${u.secondaryCount}`
                );
            });
        } else {
            lines.push(
                "No hay información de repeticiones (probablemente no hay tarjetas generadas)."
            );
        }

        lines.push("");

        // ───────────────────────────────── Tarjetas
        lines.push("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
        lines.push(`🎴 Tarjetas generadas (${cards ? cards.length : 0})`);

        // Configuración básica de tarjetas
        lines.push(
            `Tarjetas solicitadas: ${cardCountInput}  |  Modo cíclico: ${
                isCyclic ? "🔁 Activado" : "⏹️ Desactivado"
            }`
        );
        lines.push("");

        if (cards && cards.length > 0) {
            cards.forEach((card) => {
                lines.push(
                    `Tarjeta #${card.id} 🎴  (♂️ ${card.maleCount} / ♀️ ${
                        card.femaleCount
                    }) ${
                        card.maxInitialRepetition > 1
                            ? "/ r:" + card.maxInitialRepetition
                            : ""
                    })}`
                );

                const principalGenderEmoji =
                    card.principal.gender === "female" ? "♀️" : "♂️";
                const principalMembershipEmoji =
                    card.principalMembership === "belongs" ? "✅" : "❌";
                const principalMembershipLabel =
                    card.principalMembership === "belongs"
                        ? "Pertenece a la clave"
                        : "No pertenece a la clave";

                lines.push(
                    `🃏 Principal: ${card.principal.original} ${principalGenderEmoji} ${principalMembershipEmoji} ${principalMembershipLabel}`
                );
                lines.push("👥 Secundarios:");

                card.secondaries.forEach((n, index) => {
                    const genderEmoji = n.gender === "female" ? "♀️" : "♂️";
                    const membershipEmoji =
                        n.membership === "belongs" ? "✅" : "❌";
                    const membershipLabel =
                        n.membership === "belongs"
                            ? "Pertenece a la clave"
                            : "No pertenece a la clave";

                    lines.push(
                        `  ${index + 1}) ${
                            n.original
                        } ${genderEmoji} ${membershipEmoji} ${membershipLabel}`
                    );
                });

                lines.push("----------------------------------------");
                lines.push("");
            });
        } else {
            lines.push("No hay tarjetas generadas.");
        }

        const content = lines.join("\n");

        // Create file and trigger download
        const blob = new Blob([content], {
            type: "text/plain;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = "tarjetas_nombres.txt";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    };

    return (
        <Box sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="flex-start">
                {/* Left column: inputs (redesigned main entry area) */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2 }} elevation={3}>
                        <Stack spacing={2}>
                            <Typography variant="h5" gutterBottom>
                                Configuración de nombres
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Introduce la lista de nombres y ajusta la clave
                                y el número de tarjetas. Usa los botones de la
                                derecha para ver los distintos análisis.
                            </Typography>

                            <TextField
                                label="Nombres de chicos ♂️ (separados por comas)"
                                value={namesInputBoys}
                                onChange={(e) =>
                                    setNamesInputBoys(e.target.value)
                                }
                                multiline
                                minRows={2}
                                margin="normal"
                                placeholder="Ejemplo: Bruno, Juan..."
                            />
                            <TextField
                                label="Nombres de chicas ♀️ (separados por comas)"
                                value={namesInputGirls}
                                onChange={(e) =>
                                    setNamesInputGirls(e.target.value)
                                }
                                multiline
                                minRows={2}
                                margin="normal"
                                placeholder="Ejemplo: Ana, Carla..."
                            />

                            <TextField
                                label="Claves iniciales 🔑 (separados por comas)"
                                value={keysInput}
                                onChange={(e) => setKeysInput(e.target.value)}
                                multiline
                                minRows={1}
                                margin="normal"
                            />
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                            >
                                <Checkbox
                                    checked={useSpanishNames}
                                    onChange={(e) =>
                                        setUseSpanishNames(e.target.checked)
                                    }
                                />
                                <Typography variant="body2">
                                    Usar los 600 primeros nombres oficiales
                                    (mujeres y hombres) como claves adicionales.
                                </Typography>
                            </Stack>

                            <TextField
                                label="Número de tarjetas"
                                type="number"
                                value={cardCountInput}
                                onChange={(e) =>
                                    setCardCountInput(e.target.value)
                                }
                                margin="normal"
                                inputProps={{ min: 0 }}
                                helperText="Cantidad de tarjetas a generar."
                            />
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                            >
                                <Checkbox
                                    checked={isCyclic}
                                    onChange={(e) =>
                                        setIsCyclic(e.target.checked)
                                    }
                                />
                                <Typography variant="body2">
                                    Tarjetas cíclicas (1er secundario =
                                    siguiente, último = anterior).
                                </Typography>
                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>

                {/* Right column: buttons that open dialogs */}
                <Grid size={{ xs: 12, md: 5 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Clave:{" "}
                        {selectedKeyResult ? (
                            <>
                                <Chip
                                    label={selectedKeyResult.key}
                                    color="primary"
                                    size="small"
                                    sx={{ mr: 1 }}
                                />
                                Diferencia de grupos: ±{selectedKeyResult.diff}
                            </>
                        ) : (
                            <Chip
                                label="Sin clave válida"
                                color="default"
                                size="small"
                                sx={{ mr: 1 }}
                            />
                        )}
                    </Typography>
                    <Paper sx={{ p: 2 }} elevation={3}>
                        <Stack spacing={2}>
                            <Typography variant="h5" gutterBottom>
                                Herramientas y resultados
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Pulsa un botón para abrir el diálogo con la
                                información correspondiente.
                            </Typography>

                            <Stack spacing={1.5}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    disabled={initialsEntries.length === 0}
                                    onClick={() => setActiveDialog("initials")}
                                >
                                    Ver repetición de iniciales
                                </Button>

                                <Button
                                    variant="outlined"
                                    fullWidth
                                    disabled={!selectedKeyResult}
                                    onClick={() =>
                                        setActiveDialog("selectedKey")
                                    }
                                >
                                    Ver clave escogida y grupos
                                </Button>

                                <Button
                                    variant="outlined"
                                    fullWidth
                                    disabled={sortedCandidates.length === 0}
                                    onClick={() => setActiveDialog("keysList")}
                                >
                                    Ver claves probadas
                                </Button>

                                <Button
                                    variant="outlined"
                                    fullWidth
                                    disabled={cards.length === 0}
                                    onClick={() => setActiveDialog("cards")}
                                >
                                    Ver tarjetas generadas
                                </Button>

                                <Button
                                    variant="outlined"
                                    fullWidth
                                    disabled={nameUsage.length === 0}
                                    onClick={() => setActiveDialog("usage")}
                                >
                                    Ver uso de nombres
                                </Button>

                                <Button
                                    variant="outlined"
                                    fullWidth
                                    disabled={
                                        cards.length === 0 &&
                                        uniqueNames.length === 0 &&
                                        !selectedKeyResult
                                    }
                                    onClick={handleExportCardsAsText}
                                    startIcon={<DownloadIcon />}
                                >
                                    Exportar todo (TXT)
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>

            {/* Generic dialog for all different views */}
            <Dialog
                open={Boolean(activeDialog)}
                onClose={handleCloseDialog}
                fullWidth
                maxWidth={getDialogMaxWidth()}
                scroll="paper"
            >
                <DialogTitle>
                    {activeDialog === "initials" && "Repetición de iniciales"}
                    {activeDialog === "selectedKey" &&
                        "Clave escogida y grupos"}
                    {activeDialog === "keysList" &&
                        "Claves probadas ordenadas por diferencia"}
                    {activeDialog === "cards" && (
                        <Stack
                            spacing={1.5}
                            direction="row"
                            sx={{
                                display: "flex",
                                justifyContent: "flex-start",
                            }}
                        >
                            <Box sx={{ flex: 1 }}>
                                Tarjetas generadas ({cards.length})
                                {hasR2OrMore && (
                                    <Tooltip title="Hay repetición de nombres como principal (se han generado más tarjetas que nombres únicos disponibles).">
                                        <Box
                                            component="span"
                                            sx={{
                                                ml: 1,
                                                display: "inline-flex",
                                                alignItems: "center",
                                                verticalAlign: "middle",
                                            }}
                                        >
                                            ⚠️
                                        </Box>
                                    </Tooltip>
                                )}
                            </Box>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={() =>
                                    setCardsVersion((prev) => prev + 1)
                                }
                            >
                                Recalcular tarjetas
                            </Button>
                            <Button
                                variant="outlined"
                                disabled={
                                    cards.length === 0 &&
                                    uniqueNames.length === 0 &&
                                    !selectedKeyResult
                                }
                                onClick={handleExportCardsAsText}
                                startIcon={<DownloadIcon />}
                            >
                                Exportar todo (TXT)
                            </Button>
                        </Stack>
                    )}
                    {activeDialog === "usage" &&
                        "Uso de nombres como principal y secundario"}
                </DialogTitle>

                <DialogContent dividers>
                    {/* Repetición de iniciales */}
                    {activeDialog === "initials" && (
                        <Box>
                            {initialsEntries.length === 0 ? (
                                <Typography variant="body2">
                                    No hay nombres válidos.
                                </Typography>
                            ) : (
                                <List dense>
                                    {initialsEntries.map(([initial, count]) => (
                                        <ListItem key={initial}>
                                            <ListItemText
                                                primary={`${initial.toUpperCase()}: ${count} (♂️${
                                                    uniqueNames.filter(
                                                        (n) =>
                                                            n.normalized
                                                                .toUpperCase()
                                                                .startsWith(
                                                                    initial.toUpperCase()
                                                                ) &&
                                                            n.gender === "male"
                                                    ).length
                                                } / ♀️${
                                                    uniqueNames.filter(
                                                        (n) =>
                                                            n.normalized
                                                                .toUpperCase()
                                                                .startsWith(
                                                                    initial.toUpperCase()
                                                                ) &&
                                                            n.gender ===
                                                                "female"
                                                    ).length
                                                })`}
                                                sx={{
                                                    "& .MuiListItemText-primary":
                                                        {
                                                            fontWeight:
                                                                selectedKeyResult.key
                                                                    .toUpperCase()
                                                                    .includes(
                                                                        initial.toUpperCase()
                                                                    )
                                                                    ? "bold"
                                                                    : "normal",
                                                        },
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Box>
                    )}

                    {/* Clave escogida y grupos */}
                    {/* Clave escogida y grupos (versión 4 grupos) */}
                    {activeDialog === "selectedKey" && (
                        <Box>
                            {selectedKeyResult ? (
                                <Box>
                                    <Typography variant="body1" sx={{ mb: 2 }}>
                                        Clave:{" "}
                                        <Chip
                                            label={selectedKeyResult.key}
                                            color="primary"
                                            size="small"
                                            sx={{ mr: 1 }}
                                        />
                                        Diferencia de grupos: ±
                                        {selectedKeyResult.diff}
                                    </Typography>

                                    <Grid container spacing={2}>
                                        {/* Pertenece - Chico */}
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <Paper sx={{ p: 2 }} elevation={1}>
                                                <Typography
                                                    variant="subtitle1"
                                                    gutterBottom
                                                >
                                                    Pertenece🔵 • Chicos♂️ (
                                                    {
                                                        selectedKeyResult.belongs.filter(
                                                            (n) =>
                                                                n.gender ===
                                                                "male"
                                                        ).length
                                                    }
                                                    )
                                                </Typography>
                                                {selectedKeyResult.belongs.filter(
                                                    (n) => n.gender === "male"
                                                ).length === 0 ? (
                                                    <Typography variant="body2">
                                                        Ningún chico pertenece.
                                                    </Typography>
                                                ) : (
                                                    <List dense>
                                                        {selectedKeyResult.belongs
                                                            .filter(
                                                                (n) =>
                                                                    n.gender ===
                                                                    "male"
                                                            )
                                                            .map((n, index) => (
                                                                <ListItem
                                                                    key={`${n.original}-${index}`}
                                                                    disablePadding
                                                                >
                                                                    <ListItemText
                                                                        primary={
                                                                            n.original
                                                                        }
                                                                    />
                                                                </ListItem>
                                                            ))}
                                                    </List>
                                                )}
                                            </Paper>
                                        </Grid>

                                        {/* Pertenece - Chica */}
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <Paper sx={{ p: 2 }} elevation={1}>
                                                <Typography
                                                    variant="subtitle1"
                                                    gutterBottom
                                                >
                                                    Pertenece🔵 • Chicas♀️ (
                                                    {
                                                        selectedKeyResult.belongs.filter(
                                                            (n) =>
                                                                n.gender ===
                                                                "female"
                                                        ).length
                                                    }
                                                    )
                                                </Typography>
                                                {selectedKeyResult.belongs.filter(
                                                    (n) => n.gender === "female"
                                                ).length === 0 ? (
                                                    <Typography variant="body2">
                                                        Ninguna chica pertenece.
                                                    </Typography>
                                                ) : (
                                                    <List dense>
                                                        {selectedKeyResult.belongs
                                                            .filter(
                                                                (n) =>
                                                                    n.gender ===
                                                                    "female"
                                                            )
                                                            .map((n, index) => (
                                                                <ListItem
                                                                    key={`${n.original}-${index}`}
                                                                    disablePadding
                                                                >
                                                                    <ListItemText
                                                                        primary={
                                                                            n.original
                                                                        }
                                                                    />
                                                                </ListItem>
                                                            ))}
                                                    </List>
                                                )}
                                            </Paper>
                                        </Grid>

                                        {/* No pertenece - Chico */}
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <Paper sx={{ p: 2 }} elevation={1}>
                                                <Typography
                                                    variant="subtitle1"
                                                    gutterBottom
                                                >
                                                    No pertenece🔴 • Chicos♂️ (
                                                    {
                                                        selectedKeyResult.notBelongs.filter(
                                                            (n) =>
                                                                n.gender ===
                                                                "male"
                                                        ).length
                                                    }
                                                    )
                                                </Typography>
                                                {selectedKeyResult.notBelongs.filter(
                                                    (n) => n.gender === "male"
                                                ).length === 0 ? (
                                                    <Typography variant="body2">
                                                        Ningún chico está fuera.
                                                    </Typography>
                                                ) : (
                                                    <List dense>
                                                        {selectedKeyResult.notBelongs
                                                            .filter(
                                                                (n) =>
                                                                    n.gender ===
                                                                    "male"
                                                            )
                                                            .map((n, index) => (
                                                                <ListItem
                                                                    key={`${n.original}-${index}`}
                                                                    disablePadding
                                                                >
                                                                    <ListItemText
                                                                        primary={
                                                                            n.original
                                                                        }
                                                                    />
                                                                </ListItem>
                                                            ))}
                                                    </List>
                                                )}
                                            </Paper>
                                        </Grid>

                                        {/* No pertenece - Chica */}
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <Paper sx={{ p: 2 }} elevation={1}>
                                                <Typography
                                                    variant="subtitle1"
                                                    gutterBottom
                                                >
                                                    No pertenece🔴 • Chicas♀️ (
                                                    {
                                                        selectedKeyResult.notBelongs.filter(
                                                            (n) =>
                                                                n.gender ===
                                                                "female"
                                                        ).length
                                                    }
                                                    )
                                                </Typography>
                                                {selectedKeyResult.notBelongs.filter(
                                                    (n) => n.gender === "female"
                                                ).length === 0 ? (
                                                    <Typography variant="body2">
                                                        Ninguna chica está
                                                        fuera.
                                                    </Typography>
                                                ) : (
                                                    <List dense>
                                                        {selectedKeyResult.notBelongs
                                                            .filter(
                                                                (n) =>
                                                                    n.gender ===
                                                                    "female"
                                                            )
                                                            .map((n, index) => (
                                                                <ListItem
                                                                    key={`${n.original}-${index}`}
                                                                    disablePadding
                                                                >
                                                                    <ListItemText
                                                                        primary={
                                                                            n.original
                                                                        }
                                                                    />
                                                                </ListItem>
                                                            ))}
                                                    </List>
                                                )}
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                </Box>
                            ) : (
                                <Typography variant="body2">
                                    No se ha encontrado ninguna clave válida.
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Claves probadas */}
                    {activeDialog === "keysList" && (
                        <Box>
                            {sortedCandidates.length === 0 ? (
                                <Typography variant="body2">
                                    No se ha probado ninguna clave.
                                </Typography>
                            ) : (
                                <List dense>
                                    {sortedCandidates.map((c, index) => {
                                        const normalizedCandidateKey =
                                            normalizeText(c.key);

                                        let normalizedSelectedKey = "";
                                        if (manualSelectedKey) {
                                            normalizedSelectedKey =
                                                normalizeText(
                                                    manualSelectedKey
                                                );
                                        } else if (
                                            selectedKeyResult &&
                                            selectedKeyResult.key
                                        ) {
                                            normalizedSelectedKey =
                                                normalizeText(
                                                    selectedKeyResult.key
                                                );
                                        }

                                        const isSelected =
                                            normalizedCandidateKey ===
                                            normalizedSelectedKey;

                                        return (
                                            <React.Fragment
                                                key={`${c.key}-${index}`}
                                            >
                                                {index > 0 && (
                                                    <Divider component="li" />
                                                )}
                                                <ListItem
                                                    button
                                                    selected={isSelected}
                                                    onClick={() => {
                                                        setManualSelectedKey(
                                                            c.key
                                                        );
                                                        handleCloseDialog();
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={`${
                                                            index + 1
                                                        }. ${c.key}`}
                                                        primaryTypographyProps={{
                                                            sx: {
                                                                fontWeight:
                                                                    isSelected
                                                                        ? "bold"
                                                                        : "normal",
                                                            },
                                                        }}
                                                        secondary={`Diferencia: ±${
                                                            c.diff
                                                        } • ${
                                                            c.source ===
                                                            "default"
                                                                ? "Desde lista inicial"
                                                                : c.source ===
                                                                  "names"
                                                                ? "Desde los nombres"
                                                                : "Desde nombres oficiales"
                                                        }`}
                                                    />
                                                </ListItem>
                                            </React.Fragment>
                                        );
                                    })}
                                </List>
                            )}
                        </Box>
                    )}

                    {/* Tarjetas generadas */}
                    {activeDialog === "cards" && (
                        <Box>
                            {cards.length === 0 ? (
                                <Typography variant="body2">
                                    No se han podido generar tarjetas. Revisa
                                    que haya nombres, una clave válida y un
                                    número de tarjetas mayor que 0.
                                </Typography>
                            ) : (
                                <Grid container spacing={1.5}>
                                    {cards.map((card) => (
                                        <Grid
                                            key={card.id}
                                            size={{ xs: 12, sm: 6, md: 4 }}
                                        >
                                            <Paper
                                                variant="outlined"
                                                sx={{
                                                    p: 1.5,
                                                    bgcolor:
                                                        "background.default",
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle2"
                                                    gutterBottom
                                                >
                                                    <Checkbox />
                                                    Tarjeta #{card.id} •{" "}
                                                    {card.maleCount} ♂️ /{" "}
                                                    {card.femaleCount} ♀️
                                                    {card.maxInitialRepetition >
                                                        1 && (
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                fontWeight:
                                                                    "bold",
                                                                color: "error.main",
                                                            }}
                                                        >
                                                            {" "}
                                                            / r:{" "}
                                                            {
                                                                card.maxInitialRepetition
                                                            }
                                                        </Box>
                                                    )}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>
                                                        {
                                                            card.principal
                                                                .original
                                                        }
                                                    </strong>{" "}
                                                    {card.principal
                                                        .membership ===
                                                    "belongs"
                                                        ? "🔵"
                                                        : "🔴"}
                                                    {card.principal.gender ===
                                                    "male"
                                                        ? "♂️"
                                                        : "♀️"}
                                                </Typography>

                                                <List dense sx={{ pl: 1 }}>
                                                    {card.secondaries.map(
                                                        (n, idx) => (
                                                            <ListItem
                                                                key={`${card.id}-${n.normalized}-${idx}`}
                                                                disableGutters
                                                                sx={{
                                                                    p: 0,
                                                                    m: 0,
                                                                }}
                                                            >
                                                                <ListItemText
                                                                    sx={{
                                                                        p: 0,
                                                                        m: 0,
                                                                    }}
                                                                    primary={
                                                                        n.original +
                                                                        ` ${
                                                                            n.membership ===
                                                                            "belongs"
                                                                                ? "🔵"
                                                                                : "🔴"
                                                                        }${
                                                                            n.gender ===
                                                                            "male"
                                                                                ? "♂️"
                                                                                : "♀️"
                                                                        }`
                                                                    }
                                                                />
                                                            </ListItem>
                                                        )
                                                    )}
                                                </List>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Box>
                    )}

                    {/* Uso de nombres */}
                    {activeDialog === "usage" && (
                        <Box>
                            {nameUsage.length === 0 ? (
                                <Typography variant="body2">
                                    No hay tarjetas generadas, así que no hay
                                    uso de nombres que mostrar.
                                </Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Nombre</TableCell>
                                            <TableCell align="right">
                                                # principal
                                            </TableCell>
                                            <TableCell align="right">
                                                # secundario
                                            </TableCell>
                                            <TableCell align="right">
                                                Total apariciones
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {nameUsage.map((item) => (
                                            <TableRow key={item.normalized}>
                                                <TableCell>
                                                    {/* Membership + gender icons before the name */}
                                                    {membershipByNormalized[
                                                        item.normalized
                                                    ] === "notBelongs"
                                                        ? "🔴"
                                                        : "🔵"}
                                                    {item.gender === "female"
                                                        ? "♀️ "
                                                        : "♂️ "}
                                                    {item.original}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {item.principalCount}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {item.secondaryCount}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {item.totalCount}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default NamesScreen;

/**
2) Reglas que sigue el generador de tarjetas

2.1. Entrada y normalización de nombres
- Los nombres se introducen en un solo campo de texto, separados por comas.
- Se eliminan espacios extra y nombres vacíos.
- Cada nombre se normaliza internamente: se pasa a minúsculas, se quitan tildes
  y se guarda también la inicial (primer carácter del nombre normalizado).
- Para el control global de usos, los nombres se consideran por su forma
  normalizada (por ejemplo, “Álvaro” y “Alvaro” se tratan como el mismo nombre).

2.2. Selección de la clave
- Se prueban claves candidatas en este orden de prioridad:
  1) Claves escritas manualmente en el campo “Claves iniciales”.
  2) Todos los nombres de la lista actual.
  3) (Opcional) Listas de nombres oficiales españoles (mujeres y hombres).
- Para cada clave candidata se normaliza y se toman todas sus letras.
- Un nombre “pertenece a la clave” si la inicial del nombre está incluida en
  las letras de la clave (ya normalizada). Si no, “no pertenece a la clave”.
- Para cada clave se calculan dos grupos:
  - Nombres que pertenecen a la clave.
  - Nombres que no pertenecen a la clave.
- Se busca una clave donde la diferencia de tamaño entre ambos grupos sea
  mínima (idealmente 0 o 1).
- Se selecciona la primera clave con diferencia ≤ 1 dentro de las claves
  manuales. Si no la hay, se prueban los nombres de la lista y, después,
  (si está activado) los nombres oficiales.
- Si ninguna clave consigue diferencia ≤ 1, se elige la que tenga la menor
  diferencia global.
- En la interfaz se pueden seleccionar manualmente claves probadas: al hacer
  clic sobre una clave en la lista de “Claves probadas”, esa clave se fuerza
  por encima de la seleccionada automáticamente.

2.4. Reglas para escoger los nombres principales de las tarjetas
- Se forman cuatro grupos:
  - Pertenecen a la clave y son chico.
  - Pertenecen a la clave y son chica.
  - No pertenecen a la clave y son chico.
  - No pertenecen a la clave y son chica.
- A partir de esos grupos se definen:
  - `belongGroup` = todos los que pertenecen (chicos + chicas).
  - `notGroup` = todos los que no pertenecen (chicos + chicas).
- Se generan tantas tarjetas como se haya indicado, siempre que haya nombres
  suficientes.
- Si hay nombres tanto en `belongGroup` como en `notGroup`, las tarjetas
  alternan el tipo de principal:
  - Tarjeta 1: principal que pertenece.
  - Tarjeta 2: principal que no pertenece.
  - Tarjeta 3: principal que pertenece.
  - Tarjeta 4: principal que no pertenece, etc.
- Si solo hay nombres que pertenecen (o solo que no pertenecen), se usan
  sin alternancia posible.
- Dentro de cada grupo (pertenece / no pertenece) se elige como siguiente
  principal un nombre entre los que tienen menor uso como principal, para
  mantener equilibrado el número de veces que cada nombre aparece como
  principal.

2.5. Reglas para los secundarios y estructura cíclica
- Modo cíclico:
  - Si el modo cíclico está activado y hay al menos 2 tarjetas:
    - El primer secundario de una tarjeta es el principal de la siguiente.
    - El último secundario de una tarjeta es el principal de la anterior.
  - Si el modo cíclico está desactivado, los secundarios no tienen esta
    relación obligatoria con otros principales.
- Pertenencia a la clave en los secundarios:
  - Por defecto, los 5 secundarios se buscan en el grupo contrario al principal:
    - Si el principal pertenece, se priorizan secundarios que no pertenecen.
    - Si el principal no pertenece, se priorizan secundarios que sí pertenecen.
  - Si no hay suficientes nombres en el grupo contrario para completar los 5
    secundarios (respetando el resto de reglas), se relaja la restricción:
    primero se permiten secundarios del mismo grupo que el principal y, como
    último recurso, cualquier nombre disponible.
- Equilibrio de género por tarjeta:
  - El objetivo es que cada tarjeta tenga 3 chicos y 3 chicas en total
    (principal + 5 secundarios).
  - Antes de completar los secundarios “libres”, se calcula cuántos chicos
    y chicas habrá contando el principal y, si existe, el último secundario
    cíclico reservado.
  - A partir de ahí se decide cuántos chicos y cuántas chicas faltan para
    acercarse a 3 y 3, y se seleccionan secundarios que se ajusten a esa
    necesidad.
  - Si no hay suficientes nombres de un género, la tarjeta puede quedar
    desbalanceada.
- Equilibrio de uso como secundario:
  - Cada vez que un nombre se elige como secundario, se incrementa su contador
    de uso como secundario y su contador total (principal + secundario).
  - Al buscar nuevos secundarios, se priorizan nombres con menor uso total
    para mantener un reparto equilibrado entre todos.
- Regla de iniciales dentro de la tarjeta:
  - Para cada tarjeta se lleva un contador de cuántas veces aparece cada
    inicial, incluyendo:
    - El principal.
    - Los secundarios cíclicos (siguiente y anterior principal) si están.
  - Al elegir los secundarios “libres”:
    - Se prefieren nombres cuyas iniciales hayan aparecido menos veces en
      esa tarjeta.
    - Se intenta que ninguna inicial se use más de dos veces en la misma
      tarjeta.
    - Solo se supera este límite si no quedan alternativas para completar
      los 5 secundarios respetando las demás reglas.
- Segunda pasada con modo cíclico:
  - Tras una primera construcción de todas las tarjetas, puede hacerse una
    segunda pasada (cuando el modo cíclico está activo) en la que:
    - Se mantiene el mismo principal.
    - Se mantienen como fijos el primer y el último secundario (siguiente
      y anterior principal).
    - Se recalculan los secundarios intermedios para mejorar el cumplimiento
      de las reglas de género, pertenencia, equilibrio de uso y distribución
      de iniciales.

2.6. Reglas globales de uso de nombres
- Para cada nombre se registran:
  - Veces que aparece como principal.
  - Veces que aparece como secundario.
  - Total de apariciones (principal + secundario).
- Al elegir entre varios candidatos (tanto para principales como para
  secundarios), se priorizan nombres con menor uso total, para evitar que
  algunos se repitan demasiado y otros apenas aparezcan.
- Si se piden muchas tarjetas con pocos nombres de entrada, algunas reglas
  “suaves” (equilibrio perfecto 3/3, iniciales sin repetir, etc.) pueden
  no cumplirse en todas las tarjetas.
- En situaciones límite, el orden de prioridad de las reglas es:
  1) Poder generar el máximo de tarjetas posible.
  2) Respetar pertenencia a la clave y la alternancia de principales en
     la medida de lo posible.
  3) Mantener la estructura cíclica (si está activada).
  4) Mantener el equilibrio de género.
  5) Mantener el equilibrio global de uso de los nombres.
  6) Minimizar la repetición de iniciales dentro de cada tarjeta.

Bajo ninguna circunstancia se generarán tarjetas con nombres repetidos
*/
